import {
  Component,
  DestroyRef,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatchError } from 'src/app/services/errors/error-matcher';
import { SomethingWentWrongComponent } from '../../common/error/something-went-wrong/something-went-wrong.component';
import { TranslateModule } from '@ngx-translate/core';
import { LoadingButtonComponent } from '../../common/loading-button/loading-button.component';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { TagContract } from '@app-types/api/tag';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import {
  ReactiveFormsModule,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { TagComponentComponent } from '@app-components/common/tag-component/tag-component.component';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import {
  MatAutocompleteModule,
  MatAutocompleteSelectedEvent,
  MatAutocompleteTrigger,
} from '@angular/material/autocomplete';
import { MatInputModule } from '@angular/material/input';
import { AsyncPipe, NgStyle } from '@angular/common';
import { TAGS_COLORS } from '@app-shared/constants/constants';
import {
  DEFAULT_TAG_COLOR_ID,
  TagColorSelectComponent,
} from '@app-components/common/tag-color-select/tag-color-select.component';
import { BehaviorSubject, Observable } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { DrawerService } from '@app-services/drawer.service';
import { MatProgressSpinner } from '@angular/material/progress-spinner';

@Component({
  selector: 'app-add-tags-dialog',
  templateUrl: './add-tags-dialog.component.html',
  styleUrls: ['./add-tags-dialog.component.scss'],
  imports: [
    SomethingWentWrongComponent,
    TranslateModule,
    LoadingButtonComponent,
    MatIconModule,
    MatButtonModule,
    NgxSkeletonLoaderModule,
    TagComponentComponent,
    MatFormField,
    MatLabel,
    MatAutocompleteModule,
    MatInputModule,
    ReactiveFormsModule,
    TagColorSelectComponent,
    NgStyle,
    AsyncPipe,
    MatProgressSpinner,
  ],
})
export class AddTagsDialogComponent implements OnInit, OnDestroy {
  @Input() public initialTags: TagContract[] = [];
  @Input({ required: true })
  public loadTags!: (search?: string | null) => Promise<TagContract[]>;
  @Input({ required: true })
  public insertTag!: (tag: string, color: string) => Promise<TagContract>;
  @Input({ required: true })
  public saveTags!: (selectedTags: TagContract[]) => Promise<void>;
  @Input() public isDrawer = true;
  @Input() public label: string;
  @Input() public header: string;
  @Input() public buttonLabel?: string;
  @Output() public closeEventEmitter = new EventEmitter<void>();
  TAGS_COLORS = TAGS_COLORS;
  public thisTags: TagContract[] = [];
  public filteredOptions: Observable<TagContract[]>;
  public isLoading = true;
  public isLoadingAddTag = false;
  public isSearchingTags = false;
  public isSavingTags = false;
  public isError = false;
  public form = new UntypedFormGroup({
    color: new UntypedFormControl(DEFAULT_TAG_COLOR_ID, [Validators.required]),
    tag: new UntypedFormControl(''),
  });

  @ViewChild('input') tagInput!: ElementRef<HTMLInputElement>;
  @ViewChild(MatAutocompleteTrigger)
  autocompleteTrigger!: MatAutocompleteTrigger;

  private tagOptions$ = new BehaviorSubject<TagContract[]>([]);
  private loadedTags: TagContract[] = [];
  private destroyRef = inject(DestroyRef);

  constructor(
    public matchError: MatchError,
    private drawerService: DrawerService
  ) {
    this.filteredOptions = this.tagOptions$.asObservable();
  }

  async ngOnInit(): Promise<void> {
    await this.loadData();

    const tag = this.form.get('tag');
    tag?.valueChanges
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(value => {
        if (value && typeof value !== 'string') {
          this.resetTagInput();
          return;
        }
        const search = typeof value === 'string' ? value.trim() || null : null;
        void this.searchTags(search);
      });
  }

  onInputFocus(): void {
    const tagControl = this.form.get('tag');
    if (tagControl?.value && typeof tagControl.value !== 'string') {
      this.resetTagInput();
    }

    this.refreshOptions();

    if (this.tagOptions$.value.length) {
      this.autocompleteTrigger?.openPanel();
      return;
    }

    void this.searchTags(null).then(() => {
      this.autocompleteTrigger?.openPanel();
    });
  }

  private isSameTag(a: TagContract, b: TagContract): boolean {
    return a.tagId === b.tagId || a.tag.toLowerCase() === b.tag.toLowerCase();
  }

  private excludeSelectedTags(tags: TagContract[]): TagContract[] {
    return tags.filter(
      el => !this.thisTags.some(tag => this.isSameTag(tag, el))
    );
  }

  private refreshOptions(): void {
    this.tagOptions$.next(this.excludeSelectedTags(this.loadedTags));
  }

  async searchTags(search: string | null): Promise<void> {
    this.isSearchingTags = true;
    try {
      this.loadedTags = await this.loadTags(search);
      this.refreshOptions();
    } catch (e) {
      this.matchError.logError(e);
    } finally {
      this.isSearchingTags = false;
    }
  }

  async handleSave(): Promise<void> {
    this.isSavingTags = true;
    this.drawerService.disabledDrawer(true);
    try {
      await this.saveTags([...this.thisTags]);
      this.drawerService.disabledDrawer(false);
      this.isDrawer && this.drawerService.closeDrawer();
    } catch (e) {
      this.drawerService.disabledDrawer(false);
      this.matchError.errorHandler(e);
      this.matchError.logError(e);
    } finally {
      this.isSavingTags = false;
    }
  }

  public async loadData(): Promise<void> {
    this.isError = false;
    this.isLoading = true;
    try {
      this.loadedTags = await this.loadTags(null);
      this.thisTags = [...(this.initialTags ?? [])];
      this.refreshOptions();
    } catch (e) {
      this.isError = true;
      this.matchError.logError(e);
    } finally {
      this.isLoading = false;
    }
  }

  selectedTag($event: MatAutocompleteSelectedEvent): void {
    this.thisTags.push($event.option.value);
    this.resetTagInput();
    this.refreshOptions();
  }

  async addNewTag(): Promise<void> {
    try {
      this.isLoadingAddTag = true;
      const tagValue = this.getTagInputValue();
      const color = this.form.value.color;
      const created = await this.insertTag(tagValue, color);
      this.thisTags.push(created);
      this.loadedTags = [created, ...this.loadedTags];
      this.resetTagInput();
      this.refreshOptions();
      this.drawerService.disabledDrawer(false);
    } catch (e) {
      this.drawerService.disabledDrawer(false);
      this.matchError.errorHandler(e);
      this.matchError.logError(e);
    } finally {
      this.isLoadingAddTag = false;
    }
  }

  deleteTag(tag: TagContract): void {
    this.thisTags = this.thisTags.filter(
      option => !this.isSameTag(option, tag)
    );
    this.resetTagInput();
    this.refreshOptions();
    this.autocompleteTrigger?.openPanel();
  }

  private resetTagInput(): void {
    setTimeout(() => {
      if (this.tagInput?.nativeElement) {
        this.tagInput.nativeElement.value = '';
      }
      this.form.get('tag')?.setValue('', { emitEvent: false });
    });
  }

  private getTagInputValue(): string {
    const value = this.form.get('tag')?.value;
    return typeof value === 'string' ? value.trim() : '';
  }

  isDisabledAddTag(): boolean {
    const tagValue = this.getTagInputValue();
    if (!tagValue) {
      return true;
    }

    const normalized = tagValue.toLowerCase();
    return (
      this.thisTags.some(el => el.tag.toLowerCase() === normalized) ||
      this.loadedTags.some(el => el.tag.toLowerCase() === normalized)
    );
  }

  ngOnDestroy(): void {
    this.closeEventEmitter.emit();
  }
}
